import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  useOffer,
  useOfferMode,
  useOffers,
} from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { pushSimpleEvent } from "@nju/data-layer";
import { SidebarOffer } from "@nju/feature-portal-sidebar";
import { CancelOrderModal } from "@nju/feature-sales-order";
import { ErrorMessage } from "@nju/form";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import {
  A,
  Alert,
  BlockContent,
  Button,
  ButtonsWrapper,
  getAlertEsimText,
  Heading,
  Layout,
  Main,
  Modal,
  Separator,
  Side,
  Text,
  useOverlay,
} from "@nju/ui";
import { OnboardingMembersInfo } from "../OnboardingMembersInfo";
import {
  ISelectedMsisdnOfferType,
  SelectMsisdnOfferTypeForm,
  selectMsisdnOfferTypeMachine,
} from "./partials";

interface ISelectMsisdnOfferTypePageProps {
  variant?: IScope;
}

export function SelectMsisdnOfferTypePage({
  variant = "portal",
}: ISelectMsisdnOfferTypePageProps) {
  const router = useRouter();
  const mode = useOfferMode();
  const cancelOrderModal = useOverlay();
  const multiOrchestrator = useOrchestrators("multiOrder", {
    isOptional: true,
  });

  if (!multiOrchestrator) {
    throw new Error("Missing orchestrator");
  }

  const { offerId, id } = multiOrchestrator;
  const offer = useOffer(offerId);
  const { mobile } = useOffers();
  const resolvedOffer = mobile.indefiniteOffers.find((offer) => {
    return offer.display;
  });
  const price =
    resolvedOffer?.ftthBundleDiscount || resolvedOffer?.bundleDiscount;

  const [{ context, matches }, send] = useMachine(
    selectMsisdnOfferTypeMachine,
    {
      actions: {
        redirectToPrepaidOfferMigrationPage: () => {
          if (variant === "sales") {
            router.push(
              salesRoutes.migracjaNumeru.prepaid.wybierzDateMigracji.get({
                mode: "mobile",
              }),
            );
          }

          if (variant !== "sales") {
            router.push(
              portalRoutes.migracjaNumeru.prepaid.wybierzDateMigracji.get({
                mode: "mobile",
              }),
            );
          }
        },
        redirectToPostpaidOfferMigrationPage: () => {
          if (variant === "sales") {
            router.push(
              salesRoutes.migracjaNumeru.postpaid.terminMigracji.get({
                mode: "mobile",
              }),
            );
          }

          if (variant !== "sales") {
            router.push(
              portalRoutes.migracjaNumeru.postpaid.terminMigracji.get({
                mode: "mobile",
              }),
            );
          }
        },
      },
    },
  );

  const { errorMessage } = context;

  const handleMsisdnOfferTypeChange = React.useCallback(
    (value: ISelectedMsisdnOfferType) => {
      send({ type: "SET_MSISDN_OFFER_TYPE", value });
    },
    [send],
  );

  const handleNextStep = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  const handlePushDataLayer = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "process_finish_later",
    });
  }, []);

  return (
    <>
      {price && variant === "sales" && (
        <Alert iconName="info-circle" text={getAlertEsimText(price)} />
      )}
      <BlockContent>
        {variant !== "sales" && (
          <OnboardingMembersInfo
            isMulti={false}
            multiOrder={multiOrchestrator}
          />
        )}
        <Layout>
          <Main>
            <Heading
              className="sm:mb-3 mb-4"
              font="ttfirs"
              tone="neutral"
              variant="h2"
              weight="bold"
            >
              {variant !== "sales"
                ? "z jakiej oferty obecnie korzystasz?"
                : "z jakiej oferty obecnie korzysta klient?"}
            </Heading>
            <Separator size="s" tone="invisible" />
            <SelectMsisdnOfferTypeForm
              handleMsisdnOfferTypeChange={handleMsisdnOfferTypeChange}
            />
            <Text className="whitespace-pre-line">
              {variant !== "sales"
                ? "Przejście dalej oznacza upoważnienie nju do działań niezbędnych do przeniesienia Twojego numeru."
                : "Przejście dalej oznacza upoważnienie nju do działań niezbędnych \n do przeniesienia numeru klienta."}
            </Text>
            <Separator size="s" tone="invisible" />
            {errorMessage && <ErrorMessage message={errorMessage} />}
            <ButtonsWrapper>
              {variant !== "sales" && (
                <Link
                  href={portalRoutes.mojeKonto.index.get({ mode: "mobile" })}
                  passHref={true}
                >
                  <A
                    onClick={handlePushDataLayer}
                    size={{
                      "@onlyMobile": "s",
                    }}
                    variant="secondary"
                  >
                    dokończ później
                  </A>
                </Link>
              )}
              {variant === "sales" && (
                <Button
                  display="inline"
                  onPress={cancelOrderModal.open}
                  size={{
                    "@onlyMobile": "s",
                  }}
                  variant="secondary"
                >
                  anuluj
                </Button>
              )}
              <Button
                isLoading={matches("goToNextStep")}
                onPress={handleNextStep}
                size={{
                  "@onlyMobile": "s",
                }}
                type="submit"
              >
                dalej
              </Button>
            </ButtonsWrapper>
          </Main>
          <Side>
            <SidebarOffer offer={offer} orchestrator={multiOrchestrator} />
          </Side>
          {cancelOrderModal.isOpened && (
            <Modal
              onClose={cancelOrderModal.close}
              title="anulowanie zamówienia"
            >
              <CancelOrderModal
                mode={mode}
                onClose={cancelOrderModal.close}
                orchestratorId={id}
              />
            </Modal>
          )}
        </Layout>
      </BlockContent>
    </>
  );
}
